import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  clearSignOutInFlight,
  getSession,
  markSignOutInFlight,
  onAuthStateChange,
  sessionsEqual,
  signOut,
  type Session,
} from '@pc/db';

interface AuthCtx {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // Functional updater + content-equality so the no-op `SIGNED_IN`
    // that supabase-js re-emits on every tab visibility change is a
    // literal React state no-op. Without this, the new Session
    // reference would cascade through every useAuth() consumer and
    // flush brittle route-gate state machines through a Loading
    // branch, unmounting the page tree and resetting any in-progress
    // form. See sessionsEqual() docs in @pc/db for the full story.
    const applySession = (next: Session | null) => {
      if (!alive) return;
      setSession((prev) => (sessionsEqual(prev, next) ? prev : next));
    };
    getSession()
      .then(applySession)
      .finally(() => {
        if (alive) setLoading(false);
      });
    const off = onAuthStateChange((_event, s) => {
      // If the user signs back in (e.g. after a sign-out + immediate
      // re-login within 8s), clear the stale sign-out sentinel so
      // RequireAuth doesn't sit on `<Loading />` until it expires.
      if (s && s.access_token) clearSignOutInFlight();
      applySession(s);
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      loading,
      signOut: async () => {
        // Mark the sign-out as in-flight BEFORE the await so this
        // app's RequireAuth (and RequireVendor downstream) can
        // short-circuit their session=null -> auth/login redirect,
        // which would otherwise race the auth/signout navigation
        // below and win. See
        // `packages/db/src/services/auth.ts::markSignOutInFlight`.
        markSignOutInFlight();
        // Local sign-out clears `pc-sell.auth` in this tab. Bounce
        // through `auth.peptideclients.com/signout` so the central
        // `pc-auth.auth` session is also cleared, preventing any
        // other B2B app from silently re-issuing a session.
        await signOut();
        setSession(null);
        if (typeof window !== 'undefined') {
          window.location.replace(
            'https://auth.peptideclients.com/signout?return_to=' +
              encodeURIComponent('https://peptideclients.com/sell.html'),
          );
        }
      },
    }),
    [session, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>.');
  return v;
}
